import {PropsWithChildren} from "react";
import {GeometryHelper, PointDefinition} from "../../../services/onboarding-tour/geometry.helper";
import {OrbitComponent} from "../../base-components/orbit-component/orbit-component";
import {OnboardingDialogLogoBox} from "./boxes/onboarding-dialog-log/onboarding-dialog-logo.box";
import {OnboardingDialogMainTextBox} from "./boxes/onboarding-dialog-main-text.box";
import {OnboardingDialogSecondaryTextBox} from "./boxes/onboarding-dialog-secondary-text.box";
import {FlexContainerBox, FlexGrowItemBox} from "../../../themes/architecture/css-helpers/flex";
import {OnboardingDialogNextBox} from "./boxes/onboarding-dialog-next/onboarding-dialog-next.box";
import {OnboardingDialogStepIndicatorBox} from "./boxes/onboarding-dialog-step-indicator/onboarding-dialog-step-indicator.box";
import {OnboardingDialogContentBox} from "./boxes/onboarding-dialog-content.box";
import {IOnboardingStep} from "../../../services/onboarding-tour/onboarding-tour.service.interface";
import {Image} from "../../raw-components/image/image";
import {DomEventSubscription} from "../../../../utils/event-listener-registration";
import {OnboardingDialogCloseBox} from "./boxes/onboarding-dialog-close.box";
import {imagePreload} from "../../../../common/utils/image-preload";
import {CyClass} from "../../../../common/cy-classes";
import {OnboardingDialogBox} from "./boxes/onboarding-dialog/onboarding-dialog.box";
import {generateArray} from "../../../../utils/array-helper";

interface IOnboardingDialogComponentProps {
    step: IOnboardingStep;
    stepIndex: number;
    stepsTotal: number;
    zIndex: number;
    closing?: boolean;
    show: boolean;
    onLogoCoordinatesUpdate(centerCoordinates: PointDefinition | undefined): void;
    onNext(): void;
    onDone(): void;
    onClose(): void;
}

export class OnboardingDialogComponent extends OrbitComponent<PropsWithChildren<IOnboardingDialogComponentProps>> {
    private logoRef: HTMLElement | null = null;
    private resizeSubscription: DomEventSubscription | null = null;
    componentDidMount() {
        this.resizeSubscription = this.services.window.subscribeTo.resize(this.onLogoCoordinatesUpdate);
        this.services.analytics.sendOrbitUserFlowEvent("Onboarding", "Show", (this.props.stepIndex + 1).toString());
    }

    componentDidUpdate(prevProps: IOnboardingDialogComponentProps) {
        if (this.props.stepIndex !== prevProps.stepIndex) {
            this.services.analytics.sendOrbitUserFlowEvent("Onboarding", "Show", (this.props.stepIndex + 1).toString());
        }
        if (this.props.stepIndex + 1 === this.props.stepsTotal) {
            imagePreload(this.services.theme.currentTheme.assets.images.onboarding.onboarding_logo_closing_png.toString());
        }
    }

    componentWillUnmount(): void {
        this.resizeSubscription.dispose();
    }

    render() {
        const CloseIcon = this.services.theme.currentTheme.icons.closeIcon;
        const imgAssets = this.services.theme.currentTheme.assets.images.onboarding;

        const logoUrlString = this.props.closing ? imgAssets.onboarding_logo_closing_png.toString() : imgAssets.onboarding_logo_png.toString();
        return <OnboardingDialogBox className={CyClass('cy-onboarding-tour')} zIndex={this.props.zIndex} show={this.props.show} delay={this.props.show ? 300 : 0}>
            <OnboardingDialogContentBox>
                <OnboardingDialogLogoBox ref={this.onLogoRefCapture} zIndex={this.props.zIndex+2}>
                    <Image src={logoUrlString} />
                </OnboardingDialogLogoBox>
                <OnboardingDialogCloseBox onClick={this.onClose}>
                    <CloseIcon size={1.8} />
                </OnboardingDialogCloseBox>
                <OnboardingDialogMainTextBox>{this.services.translation.translate(this.props.step.mainText)}</OnboardingDialogMainTextBox>
                <OnboardingDialogSecondaryTextBox>{this.services.translation.translate(this.props.step.secondaryText)}</OnboardingDialogSecondaryTextBox>
            </OnboardingDialogContentBox>
            <FlexContainerBox alignItems="center">
                {generateArray(this.props.stepsTotal, this.props.stepIndex).map((sIdx, idx) => <OnboardingDialogStepIndicatorBox key={idx} activeStep={sIdx === idx} />)}
                <FlexGrowItemBox />
                {
                    this.props.stepIndex === this.props.stepsTotal - 1 ?
                    <OnboardingDialogNextBox onClick={this.onDone}>{this.services.translation.translate("DONE")}</OnboardingDialogNextBox> :
                    <OnboardingDialogNextBox onClick={this.props.onNext}>{this.services.translation.translate("NEXT")}</OnboardingDialogNextBox>
                }
            </FlexContainerBox>
            {this.props.children}
        </OnboardingDialogBox>;
    }

    private onLogoRefCapture = (el: HTMLDivElement | null) => {
        this.logoRef = el;
        this.onLogoCoordinatesUpdate();
    };
    private onLogoCoordinatesUpdate = () => {
        if (this.logoRef) {
            // compute the center of the div coordinates
            let r = this.logoRef.getBoundingClientRect();
            let c = GeometryHelper.getCenterOfDOMRect(r);
            this.props.onLogoCoordinatesUpdate(c);
        } else {
            this.props.onLogoCoordinatesUpdate(undefined)
        }
    };
    private onClose = () => {
        this.services.analytics.sendOrbitUserFlowEvent("Onboarding", "Click_Close", (this.props.stepIndex + 1).toString());
        this.props.onClose();
    }
    private onDone = () => {
        this.services.analytics.sendOrbitUserFlowEvent("Onboarding", "Click_Done", (this.props.stepIndex + 1).toString());
        this.props.onDone()
    }
}